// Foundations
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

// rxjs
import { Observable } from 'rxjs';

// Services

// Models
import { CognitoUserModel } from '../models/cognito-user.model';
import { CognitoStatus } from '../models/cognito-status.model';

// Extensions
import {
  CognitoUserPool,
  CognitoUserAttribute,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

// ENV
import { environment } from '../../environments/environment';

const COGNITO_CONFIG = environment.cognitoConfig;

export const FLAG_ACCT_IN_DD = 'custom:account_in_data_dawg';

export const getLastAuthUser = (): string => {
  const lastAuthUserKey = `CognitoIdentityServiceProvider.${COGNITO_CONFIG.ClientId}.LastAuthUser`;

  const localAuthUserValue = localStorage.getItem(lastAuthUserKey);
  const sessionAuthUserValue = sessionStorage.getItem(lastAuthUserKey);

  if (localAuthUserValue !== null && sessionAuthUserValue === null) {
    // user session data stored in localStorage
    return localAuthUserValue;
  } else if (localAuthUserValue === null && sessionAuthUserValue !== null) {
    // user session data stored in sessionStorage
    return sessionAuthUserValue;
  }
  return null;
};

export const getAccessToken = (): string => {
  const cognitoClientIdPrefix = `CognitoIdentityServiceProvider.${COGNITO_CONFIG.ClientId}.`;
  const lastAuthUserKey = cognitoClientIdPrefix + 'LastAuthUser';

  const localAuthUserValue = localStorage.getItem(lastAuthUserKey);
  const sessionAuthUserValue = sessionStorage.getItem(lastAuthUserKey);

  if (localAuthUserValue !== null && sessionAuthUserValue === null) {
    // user session data stored in localStorage
    return localStorage.getItem(
      cognitoClientIdPrefix + localAuthUserValue + '.accessToken'
    );
  } else if (localAuthUserValue === null && sessionAuthUserValue !== null) {
    // user session data stored in sessionStorage
    return sessionStorage.getItem(
      cognitoClientIdPrefix + sessionAuthUserValue + '.accessToken'
    );
  }
  return null;
};

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  rememberMe = false;

  constructor(private snackBar: MatSnackBar) {}

  signUp(user: CognitoUserModel): Observable<CognitoStatus> {
    const { username, password } = user;
    const attrEmail = new CognitoUserAttribute({
      Name: 'email',
      Value: username,
    });
    const attrAcctInDD = new CognitoUserAttribute({
      Name: FLAG_ACCT_IN_DD,
      Value: 'FALSE',
    });

    return new Observable<CognitoStatus>((subscriber) => {
      this.getUserPool().signUp(
        username,
        password,
        [attrEmail, attrAcctInDD],
        null,
        (err, result) => {
          if (err) {
            subscriber.next({ result: err.name, message: err.message });
          } else {
            subscriber.next({ result: 'SUCCESS' });
          }
          subscriber.complete();
        }
      );
    });
  }

  confirmSignUp(username: string, code: string): Observable<CognitoStatus> {
    return new Observable<CognitoStatus>((subscriber) => {
      this.getCognitoUser(username).confirmRegistration(
        code,
        true,
        (err, result) => {
          if (err) {
            subscriber.next({ result: err.name, message: err.message });
          } else {
            subscriber.next({ result: 'SUCCESS' });
          }
          subscriber.complete();
        }
      );
    });
  }

  signIn(user: CognitoUserModel): Observable<CognitoStatus> {
    const { username, password } = user;
    return new Observable<CognitoStatus>((subscriber) => {
      this.getCognitoUser(username).authenticateUser(
        this.getAuthenticationDetails(username, password),
        {
          onSuccess: (session) => {
            subscriber.next({ result: 'SUCCESS' });
            subscriber.complete();
          },
          onFailure: (err) => {
            subscriber.next({ result: err.name, message: err.message });
            subscriber.complete();
          },
        }
      );
    });
  }

  signOut() {
    this.getUserPool().getCurrentUser().signOut();
  }

  forgotPassword(username: string) {
    this.getCognitoUser(username).forgotPassword({
      onSuccess: (data) => {},
      onFailure: (err) => {
        this.snackBar.open(err.message || JSON.stringify(err), '', {
          duration: 2000,
        });
      },
    });
  }

  confirmPassword(
    username: string,
    code: string,
    newPassword: string
  ): Observable<CognitoStatus> {
    return new Observable<CognitoStatus>((subscriber) => {
      this.getCognitoUser(username).confirmPassword(code, newPassword, {
        onSuccess: () => {
          subscriber.next({ result: 'SUCCESS' });
          subscriber.complete();
        },
        onFailure: (err) => {
          subscriber.next({ result: err.name, message: err.message });
          subscriber.complete();
        },
      });
    });
  }

  changePassword(
    oldPassword: string,
    newPassword: string
  ): Observable<CognitoStatus> {
    const currentUser = this.getUserPool().getCurrentUser();
    return new Observable<CognitoStatus>((subscriber) => {
      currentUser.authenticateUser(
        this.getAuthenticationDetails(currentUser.getUsername(), oldPassword),
        {
          onSuccess: (session) => {
            currentUser.changePassword(
              oldPassword,
              newPassword,
              (err, result) => {
                if (err) {
                  subscriber.next({ result: err.name, message: err.message });
                } else {
                  subscriber.next({ result: 'SUCCESS' });
                }
                subscriber.complete();
              }
            );
          },
          onFailure: (err) => {
            subscriber.next({ result: err.name, message: err.message });
            subscriber.complete();
          },
        }
      );
    });
  }

  resendCode(username: string) {
    this.getCognitoUser(username).resendConfirmationCode((err, result) => {
      if (err) {
        this.snackBar.open(err.message || JSON.stringify(err), '', {
          duration: 2000,
        });
        return;
      }
    });
  }

  getCognitoAttrValue(
    user: CognitoUserModel,
    attrName: string
  ): Observable<CognitoStatus> {
    const { username, password } = user;
    const tempUser = this.getCognitoUser(username);

    return new Observable<CognitoStatus>((subscriber) => {
      tempUser.authenticateUser(
        this.getAuthenticationDetails(username, password),
        {
          onSuccess: (session) => {
            tempUser.getUserAttributes((err, attrList) => {
              if (err) {
                subscriber.next({ result: err.name, message: err.message });
                subscriber.complete();
              } else {
                const attr = attrList.find(
                  (attribute) => attribute.getName() === attrName
                );
                if (attr) {
                  subscriber.next({ result: attr.getValue() });
                } else {
                  subscriber.next({ result: 'NOT FOUND' });
                }
                subscriber.complete();
              }
            });
          },
          onFailure: (error) => {
            subscriber.next({ result: error.name, message: error.message });
            subscriber.complete();
          },
        }
      );
    });
  }

  updateCognitoAttrValue(
    user: CognitoUserModel,
    attrName: string,
    attrValue: string
  ): Observable<CognitoStatus> {
    const { username, password } = user;
    const tempUser = this.getCognitoUser(username);
    const attr = new CognitoUserAttribute({
      Name: attrName,
      Value: attrValue,
    });

    return new Observable<CognitoStatus>((subscriber) => {
      tempUser.authenticateUser(
        this.getAuthenticationDetails(username, password),
        {
          onSuccess: (session) => {
            tempUser.updateAttributes([attr], (err, result) => {
              if (err) {
                subscriber.next({ result: err.name, message: err.message });
              } else {
                subscriber.next({ result: 'SUCCESS' });
              }
              subscriber.complete();
            });
          },

          onFailure: (err) => {
            subscriber.next({ result: err.name, message: err.message });
            subscriber.complete();
          },
        }
      );
    });
  }

  deleteUser(user: CognitoUserModel): Observable<CognitoStatus> {
    const { username, password } = user;
    const tempUser = this.getCognitoUser(username);

    return new Observable<CognitoStatus>((subscriber) => {
      tempUser.authenticateUser(
        this.getAuthenticationDetails(username, password),
        {
          onSuccess: (session) => {
            tempUser.deleteUser((err, result) => {
              if (err) {
                subscriber.next({ result: err.name, message: err.message });
              } else {
                subscriber.next({ result: 'SUCCESS' });
              }
              subscriber.complete();
            });
          },
          onFailure: (err) => {
            subscriber.next({ result: err.name, message: err.message });
            subscriber.complete();
          },
        }
      );
    });
  }

  isSessionValid(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const currUser = this.getUserPool().getCurrentUser();

      if (!currUser) {
        subscriber.next(false);
        subscriber.complete();
        return;
      }

      currUser.getSession((err, session) => {
        if (err) {
          this.snackBar.open(err.message || JSON.stringify(err), '', {
            duration: 2000,
          });
          subscriber.next(false);
          subscriber.complete();
          return;
        }
        if (session.isValid()) {
          subscriber.next(true);
        } else {
          // if user session not valid, refresh session with RefreshToken
          currUser.refreshSession(
            session.getRefreshToken(),
            (error2, session2) => {
              if (error2) {
                this.snackBar.open(
                  error2.message || JSON.stringify(error2),
                  '',
                  {
                    duration: 2000,
                  }
                );
                subscriber.next(false);
                subscriber.complete();
                return;
              }
              subscriber.next(session2.isValid());
            }
          );
        }
        subscriber.complete();
      });
    });
  }

  // Private Methods

  private getUserPool(): CognitoUserPool {
    return new CognitoUserPool({
      ...COGNITO_CONFIG,
      Storage: this.getStorageOption(),
    });
  }

  private getCognitoUser(username: string): CognitoUser {
    return new CognitoUser({
      Username: username,
      Pool: this.getUserPool(),
      Storage: this.getStorageOption(),
    });
  }

  private getAuthenticationDetails(
    username: string,
    password: string
  ): AuthenticationDetails {
    return new AuthenticationDetails({
      Username: username,
      Password: password,
    });
  }

  private getStorageOption(): Storage {
    return this.rememberMe ||
      !!localStorage.getItem(
        `CognitoIdentityServiceProvider.${COGNITO_CONFIG.ClientId}.LastAuthUser`
      )
      ? localStorage
      : sessionStorage;
  }
}
